import React, { useState } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const FlightTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [forceRender, setForceRender] = useState(0); // 🌟 เพิ่ม State บังคับรีเรนเดอร์หน้าจอ

  const initialFormState = {
    flightNo: "",
    airline: "",
    depDate: trip.start || "",
    depTime: "",
    depCode: "",
    depCity: "",
    arrDate: trip.start || "",
    arrTime: "",
    arrCode: "",
    arrCity: "",
    bookingRef: "",
    notes: "",
    price: "",
    ticketLink: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  const handleTimeInput = (e, field) => {
    let val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length >= 3) {
      val = val.slice(0, 2) + ":" + val.slice(2, 4);
    }
    setFormData({ ...formData, [field]: val });
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("th-TH", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const handleEditClick = (flight) => {
    setFormData({
      ...initialFormState,
      ...flight,
    });
    setEditingId(flight.id);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData(initialFormState);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const flightData = {
        ...formData,
        id: editingId || `flight_${Date.now()}`,
        price: parseFloat(formData.price || 0),
        order: editingId
          ? trip.flightList?.find((f) => f.id === editingId)?.order
          : trip.flightList?.length || 0,
      };

      let newList;
      if (editingId) {
        newList = (trip.flightList || []).map((f) =>
          f.id === editingId ? flightData : f
        );
      } else {
        newList = [...(trip.flightList || []), flightData];
      }

      await updateDoc(doc(db, "trips", trip.id), { flightList: newList });
      onUpdateTrip(newList, "flightList");
      setForceRender((prev) => prev + 1); // 🌟 สั่งรีเรนเดอร์
      handleCancel();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบเที่ยวบินนี้ใช่หรือไม่?")) {
      const newList = (trip.flightList || []).filter((f) => f.id !== id);
      await updateDoc(doc(db, "trips", trip.id), { flightList: newList });
      onUpdateTrip(newList, "flightList");
      setForceRender((prev) => prev + 1); // 🌟 สั่งรีเรนเดอร์
    }
  };

  // 🌟 ฟังก์ชันจัดการเมื่อลากแล้วปล่อย
  const onDragEnd = async (result) => {
    if (!result.destination) return; // ถ้าลากไปปล่อยนอกกรอบ ให้ยกเลิก

    // ก๊อปปี้ข้อมูลมาเพื่อจัดลำดับใหม่
    const items = Array.from(sortedFlights);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // อัปเดตตัวแปร order ของทุกอันใหม่ทั้งหมด
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    try {
      await updateDoc(doc(db, "trips", trip.id), { flightList: updatedItems });
      onUpdateTrip(updatedItems, "flightList");
      setForceRender((prev) => prev + 1); // 🌟 กระตุ้นให้หน้าจอเปลี่ยนตำแหน่งทันที
    } catch (error) {
      console.error("Error updating flight order:", error);
    }
  };

  // 🌟 ก๊อปปี้ Array ออกมาก่อน และจัดเรียง
  const sortedFlights = [...(trip.flightList || [])].sort((a, b) => {
    if (typeof a.order === "number" && typeof b.order === "number") {
      return a.order - b.order;
    }
    return (
      new Date(`${a.depDate}T${a.depTime || "00:00"}`) -
      new Date(`${b.depDate}T${b.depTime || "00:00"}`)
    );
  });

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const formBgStyle = {
    backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
    borderRadius: "12px",
    padding: "20px",
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#ddd")}`,
    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    marginBottom: "15px",
  };

  return (
    <div style={{ paddingBottom: "30px", maxWidth: "800px", margin: "0 auto" }}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="flights-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              {sortedFlights.map((flight, index) => {
                const isEditingThis = editingId === flight.id;

                return (
                  <Draggable
                    key={String(flight.id)}
                    draggableId={String(flight.id)}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{ ...provided.draggableProps.style }}
                      >
                        {isEditingThis ? (
                          /* ================= EDIT MODE ================= */
                          <div style={formBgStyle}>
                            <form onSubmit={handleSave}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  marginBottom: "15px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "10px",
                                    flex: 1,
                                  }}
                                >
                                  <input
                                    type="text"
                                    placeholder="Flight No (เช่น TG 642)"
                                    value={formData.flightNo}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        flightNo: e.target.value,
                                      })
                                    }
                                    style={inputStyle}
                                    required
                                  />
                                  <input
                                    type="text"
                                    placeholder="Airline (เช่น Thai Airways)"
                                    value={formData.airline}
                                    onChange={(e) =>
                                      setFormData({
                                        ...formData,
                                        airline: e.target.value,
                                      })
                                    }
                                    style={{ ...inputStyle, flex: 2 }}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(flight.id)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: "18px",
                                    color: "#e74c3c",
                                    padding: "0 10px",
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "20px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: "2 1 400px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                  }}
                                >
                                  <div>
                                    <label style={labelStyle}>ออกเดินทาง</label>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <input
                                        type="date"
                                        value={formData.depDate}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            depDate: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                        required
                                      />
                                      <input
                                        type="text"
                                        placeholder="00:00"
                                        maxLength="5"
                                        value={formData.depTime}
                                        onChange={(e) =>
                                          handleTimeInput(e, "depTime")
                                        }
                                        style={{
                                          ...inputStyle,
                                          width: "80px",
                                          textAlign: "center",
                                        }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="CODE (BKK)"
                                        value={formData.depCode}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            depCode:
                                              e.target.value.toUpperCase(),
                                          })
                                        }
                                        style={{
                                          ...inputStyle,
                                          width: "100px",
                                        }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="เมือง (Bangkok)"
                                        value={formData.depCity}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            depCity: e.target.value,
                                          })
                                        }
                                        style={{ ...inputStyle, flex: 1 }}
                                      />
                                    </div>
                                  </div>

                                  <div>
                                    <label style={labelStyle}>มาถึง</label>
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                      }}
                                    >
                                      <input
                                        type="date"
                                        value={formData.arrDate}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            arrDate: e.target.value,
                                          })
                                        }
                                        style={inputStyle}
                                        required
                                      />
                                      <input
                                        type="text"
                                        placeholder="00:00"
                                        maxLength="5"
                                        value={formData.arrTime}
                                        onChange={(e) =>
                                          handleTimeInput(e, "arrTime")
                                        }
                                        style={{
                                          ...inputStyle,
                                          width: "80px",
                                          textAlign: "center",
                                        }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="CODE (NRT)"
                                        value={formData.arrCode}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            arrCode:
                                              e.target.value.toUpperCase(),
                                          })
                                        }
                                        style={{
                                          ...inputStyle,
                                          width: "100px",
                                        }}
                                      />
                                      <input
                                        type="text"
                                        placeholder="เมือง (Tokyo)"
                                        value={formData.arrCity}
                                        onChange={(e) =>
                                          setFormData({
                                            ...formData,
                                            arrCity: e.target.value,
                                          })
                                        }
                                        style={{ ...inputStyle, flex: 1 }}
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    flex: "1 1 250px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "10px",
                                    borderLeft: `1px solid ${
                                      isDarkMode ? "#444" : "#ddd"
                                    }`,
                                    paddingLeft: "20px",
                                  }}
                                >
                                  <div>
                                    <label style={labelStyle}>
                                      หมายเลขยืนยัน (Booking Ref)
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.bookingRef}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          bookingRef: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      บันทึกเพิ่มเติม
                                    </label>
                                    <input
                                      type="text"
                                      value={formData.notes}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          notes: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      ค่าใช้จ่าย (฿)
                                    </label>
                                    <input
                                      type="number"
                                      value={formData.price}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          price: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                  <div>
                                    <label style={labelStyle}>
                                      🔗 Google Drive Link / E-Ticket
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="วางลิงก์ที่นี่..."
                                      value={formData.ticketLink}
                                      onChange={(e) =>
                                        setFormData({
                                          ...formData,
                                          ticketLink: e.target.value,
                                        })
                                      }
                                      style={inputStyle}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "10px",
                                  marginTop: "20px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={handleCancel}
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                    border: `1px solid ${
                                      isDarkMode ? "#555" : "#ccc"
                                    }`,
                                    background: isDarkMode ? "#333" : "white",
                                    color: isDarkMode ? "#fff" : "#000",
                                    cursor: "pointer",
                                  }}
                                >
                                  ยกเลิก
                                </button>
                                <button
                                  type="submit"
                                  style={{
                                    padding: "8px 24px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background: "#3b82f6",
                                    color: "white",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                  }}
                                >
                                  บันทึก
                                </button>
                              </div>
                            </form>
                          </div>
                        ) : (
                          /* ================= VIEW MODE ================= */
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "15px",
                              opacity: snapshot.isDragging ? 0.8 : 1,
                            }}
                          >
                            {/* 🌟 จุดจับลาก (Grip) ที่ใช้งานได้จริง */}
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                color: "#bbb",
                                marginTop: "15px",
                                cursor: "grab",
                              }}
                            >
                              ⋮⋮
                            </div>

                            <div
                              style={{
                                flex: 1,
                                backgroundColor:
                                  theme?.cardBg ||
                                  (isDarkMode ? "#1e1e1e" : "#fff"),
                                borderRadius: "12px",
                                padding: "18px 20px",
                                border: `1px solid ${
                                  snapshot.isDragging
                                    ? "#3b82f6"
                                    : theme?.border || "#eee"
                                }`,
                                position: "relative",
                                transition: "all 0.2s",
                                boxShadow: snapshot.isDragging
                                  ? "0 10px 20px rgba(0,0,0,0.15)"
                                  : "none",
                              }}
                            >
                              <button
                                onClick={() => handleEditClick(flight)}
                                style={{
                                  position: "absolute",
                                  top: "15px",
                                  right: "15px",
                                  background: "none",
                                  border: "none",
                                  color: "#888",
                                  fontSize: "18px",
                                  cursor: "pointer",
                                }}
                              >
                                •••
                              </button>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "15px",
                                  fontSize: "20px",
                                  fontWeight: "900",
                                  color: theme?.text || "#333",
                                }}
                              >
                                <span>{flight.depCode || "BKK"}</span>
                                <span
                                  style={{ color: "#bbb", fontSize: "16px" }}
                                >
                                  ➔
                                </span>
                                <span>{flight.arrCode || "NRT"}</span>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: "45px",
                                  fontSize: "13px",
                                  color: "#888",
                                  marginBottom: "12px",
                                  marginTop: "2px",
                                }}
                              >
                                <span>{flight.depCity || "Bangkok"}</span>
                                <span>{flight.arrCity || "Tokyo"}</span>
                              </div>

                              <div
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "bold",
                                  color: theme?.text || "#333",
                                  marginBottom: "10px",
                                }}
                              >
                                {formatDisplayDate(flight.depDate)} •{" "}
                                {flight.depTime || "00:00"} —{" "}
                                {flight.arrTime || "00:00"}
                                {flight.depDate !== flight.arrDate && (
                                  <sup
                                    style={{
                                      color: "#e74c3c",
                                      marginLeft: "2px",
                                    }}
                                  >
                                    +1
                                  </sup>
                                )}
                              </div>

                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#888",
                                  textTransform: "uppercase",
                                  fontWeight: "bold",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span>
                                  {flight.airline} {flight.flightNo}
                                </span>

                                {/* 🌟 ส่วนที่แก้ไข: เพิ่มราคา และจัดกลุ่มกับปุ่มดูตั๋ว */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  {flight.price > 0 && (
                                    <span
                                      style={{
                                        color: "#10b981",
                                        fontSize: "14px",
                                        fontWeight: "900",
                                        backgroundColor: isDarkMode
                                          ? "rgba(16, 185, 129, 0.1)"
                                          : "#f0fdf4",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      ฿{Number(flight.price).toLocaleString()}
                                    </span>
                                  )}

                                  {flight.ticketLink && (
                                    <a
                                      href={flight.ticketLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        color: "#3b82f6",
                                        textDecoration: "none",
                                        backgroundColor:
                                          "rgba(59, 130, 246, 0.1)",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        textTransform: "none",
                                      }}
                                    >
                                      🔗 ดูตั๋ว
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* ================= ADD NEW FLIGHT FORM ================= */}
      {isAdding && (
        <div
          style={{
            ...formBgStyle,
            border: `2px dashed #3b82f6`,
            marginTop: "10px",
          }}
        >
          <h4 style={{ margin: "0 0 15px 0", color: "#3b82f6" }}>
            ✈️ เพิ่มเที่ยวบินใหม่
          </h4>
          <form onSubmit={handleSave}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "15px",
              }}
            >
              <div style={{ display: "flex", gap: "10px", flex: 1 }}>
                <input
                  type="text"
                  placeholder="Flight No (เช่น TG 642)"
                  value={formData.flightNo}
                  onChange={(e) =>
                    setFormData({ ...formData, flightNo: e.target.value })
                  }
                  style={inputStyle}
                  required
                />
                <input
                  type="text"
                  placeholder="Airline (เช่น Thai Airways)"
                  value={formData.airline}
                  onChange={(e) =>
                    setFormData({ ...formData, airline: e.target.value })
                  }
                  style={{ ...inputStyle, flex: 2 }}
                />
              </div>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
              <div
                style={{
                  flex: "2 1 400px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                }}
              >
                <div>
                  <label style={labelStyle}>ออกเดินทาง</label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="date"
                      value={formData.depDate}
                      onChange={(e) =>
                        setFormData({ ...formData, depDate: e.target.value })
                      }
                      style={inputStyle}
                      required
                    />
                    <input
                      type="text"
                      placeholder="00:00"
                      maxLength="5"
                      value={formData.depTime}
                      onChange={(e) => handleTimeInput(e, "depTime")}
                      style={{
                        ...inputStyle,
                        width: "80px",
                        textAlign: "center",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="CODE (BKK)"
                      value={formData.depCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          depCode: e.target.value.toUpperCase(),
                        })
                      }
                      style={{ ...inputStyle, width: "100px" }}
                    />
                    <input
                      type="text"
                      placeholder="เมือง (Bangkok)"
                      value={formData.depCity}
                      onChange={(e) =>
                        setFormData({ ...formData, depCity: e.target.value })
                      }
                      style={{ ...inputStyle, flex: 1 }}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>มาถึง</label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type="date"
                      value={formData.arrDate}
                      onChange={(e) =>
                        setFormData({ ...formData, arrDate: e.target.value })
                      }
                      style={inputStyle}
                      required
                    />
                    <input
                      type="text"
                      placeholder="00:00"
                      maxLength="5"
                      value={formData.arrTime}
                      onChange={(e) => handleTimeInput(e, "arrTime")}
                      style={{
                        ...inputStyle,
                        width: "80px",
                        textAlign: "center",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="CODE (NRT)"
                      value={formData.arrCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          arrCode: e.target.value.toUpperCase(),
                        })
                      }
                      style={{ ...inputStyle, width: "100px" }}
                    />
                    <input
                      type="text"
                      placeholder="เมือง (Tokyo)"
                      value={formData.arrCity}
                      onChange={(e) =>
                        setFormData({ ...formData, arrCity: e.target.value })
                      }
                      style={{ ...inputStyle, flex: 1 }}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  flex: "1 1 250px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  borderLeft: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                  paddingLeft: "20px",
                }}
              >
                <div>
                  <label style={labelStyle}>หมายเลขยืนยัน (Booking Ref)</label>
                  <input
                    type="text"
                    value={formData.bookingRef}
                    onChange={(e) =>
                      setFormData({ ...formData, bookingRef: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>บันทึกเพิ่มเติม</label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>ค่าใช้จ่าย (฿)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({ ...formData, price: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    🔗 Google Drive Link / E-Ticket
                  </label>
                  <input
                    type="text"
                    placeholder="วางลิงก์ที่นี่..."
                    value={formData.ticketLink}
                    onChange={(e) =>
                      setFormData({ ...formData, ticketLink: e.target.value })
                    }
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginTop: "20px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
                  background: isDarkMode ? "#333" : "white",
                  color: isDarkMode ? "#fff" : "#000",
                  cursor: "pointer",
                }}
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                style={{
                  padding: "8px 24px",
                  borderRadius: "8px",
                  border: "none",
                  background: "#3b82f6",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                บันทึก
              </button>
            </div>
          </form>
        </div>
      )}

      {!isAdding && !editingId && (
        <button
          onClick={() => {
            setFormData(initialFormState);
            setIsAdding(true);
          }}
          style={{
            padding: "15px",
            borderRadius: "12px",
            border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
            background: "transparent",
            color: isDarkMode ? "#aaa" : "#666",
            fontSize: "15px",
            fontWeight: "bold",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginTop: "10px",
            transition: "0.2s",
          }}
        >
          ➕ เพิ่มไฟล์ทใหม่
        </button>
      )}
    </div>
  );
};

export default FlightTab;
